import { stableFingerprint } from './random.js';
import { DEFAULT_DUNGEON_ENTRANCE_RADII, DUNGEON_ENTRANCE_PART_KINDS, DUNGEON_ENTRANCE_PRIMITIVES, } from './entrance-types.js';
const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;
const RESERVED_DECORATIVE_HUES = new Set(['#e8443a', '#39c46a']);
function issue(problems, code, path, message) {
    problems.push({ code, path, message });
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function validId(value) {
    return typeof value === 'string' && KEBAB_CASE.test(value);
}
function finitePositive(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
function finitePosition(value) {
    return (isRecord(value) &&
        ['x', 'y', 'z'].every((axis) => typeof value[axis] === 'number' && Number.isFinite(value[axis])));
}
function duplicates(values) {
    const seen = new Set();
    const repeated = new Set();
    for (const value of values) {
        if (seen.has(value.id))
            repeated.add(value.id);
        seen.add(value.id);
    }
    return [...repeated].sort();
}
export function validateDungeonEntranceRadii(radii, path = 'radii') {
    const problems = [];
    if (!isRecord(radii)) {
        issue(problems, 'invalid-entrance-radii', path, 'must be an object');
        return problems;
    }
    const ordered = [
        ['interactionRadius', radii['interactionRadius']],
        ['previewRevealRadius', radii['previewRevealRadius']],
        ['generationRadius', radii['generationRadius']],
        ['resetRadius', radii['resetRadius']],
    ];
    for (const [name, value] of ordered) {
        if (!finitePositive(value)) {
            issue(problems, 'invalid-radius', `${path}.${name}`, 'must be a finite number greater than 0');
        }
    }
    if (ordered.every((entry) => finitePositive(entry[1])) &&
        !(ordered[0][1] < ordered[1][1] &&
            ordered[1][1] < ordered[2][1] &&
            ordered[2][1] < ordered[3][1])) {
        issue(problems, 'invalid-radius-order', path, 'must satisfy interaction < preview reveal < generation < reset');
    }
    return problems;
}
export function validateDungeonShrineProfiles(value, difficultyIds) {
    const problems = [];
    if (!Array.isArray(value) || value.length === 0) {
        issue(problems, 'invalid-shrine-profiles', 'shrineProfiles', 'must be a non-empty array');
        return problems;
    }
    const profiles = value;
    for (const repeated of duplicates(profiles)) {
        issue(problems, 'duplicate-id', 'shrineProfiles', `duplicate id '${repeated}'`);
    }
    const difficultySeen = new Set();
    for (const [index, profile] of profiles.entries()) {
        const path = `shrineProfiles[${index}]`;
        if (!isRecord(profile)) {
            issue(problems, 'invalid-shrine-profile', path, 'must be an object');
            continue;
        }
        if (!validId(profile.id))
            issue(problems, 'invalid-id', `${path}.id`, 'must be kebab-case');
        if (!validId(profile.difficultyId)) {
            issue(problems, 'invalid-id', `${path}.difficultyId`, 'must be kebab-case');
        }
        else {
            if (difficultySeen.has(profile.difficultyId)) {
                issue(problems, 'duplicate-shrine-difficulty', `${path}.difficultyId`, `difficulty '${profile.difficultyId}' already has a shrine profile`);
            }
            difficultySeen.add(profile.difficultyId);
            if (difficultyIds && !difficultyIds.has(profile.difficultyId)) {
                issue(problems, 'unknown-difficulty', `${path}.difficultyId`, `unknown difficulty '${profile.difficultyId}'`);
            }
        }
        if (typeof profile.name !== 'string' || profile.name.length === 0) {
            issue(problems, 'invalid-name', `${path}.name`, 'must be non-empty');
        }
        for (const field of ['rank', 'platformTiers', 'archLayers', 'columnPairs']) {
            if (!Number.isInteger(profile[field]) || profile[field] < 1) {
                issue(problems, 'invalid-positive-integer', `${path}.${field}`, 'must be >= 1');
            }
        }
        for (const field of ['buttressPairs', 'spireCount']) {
            if (!Number.isInteger(profile[field]) || profile[field] < 0) {
                issue(problems, 'invalid-nonnegative-integer', `${path}.${field}`, 'must be >= 0');
            }
        }
        for (const field of [
            'scale',
            'footprintRadius',
            'portalWidth',
            'portalHeight',
            'portalIntensity',
        ]) {
            if (!finitePositive(profile[field])) {
                issue(problems, 'invalid-positive-number', `${path}.${field}`, 'must be > 0');
            }
        }
        if (!['none', 'fin', 'split', 'broken-crown'].includes(profile.crown)) {
            issue(problems, 'invalid-crown', `${path}.crown`, String(profile.crown));
        }
        if (!isRecord(profile.materials)) {
            issue(problems, 'invalid-materials', `${path}.materials`, 'must be an object');
        }
        else {
            for (const key of [
                'stone',
                'trim',
                'panel',
                'portalQuiet',
                'portalReady',
                'portalSealed',
                'interact',
            ]) {
                const colour = profile.materials[key];
                if (typeof colour !== 'string' || !HEX_COLOUR.test(colour)) {
                    issue(problems, 'invalid-colour', `${path}.materials.${key}`, 'must be #rrggbb');
                }
                else if (key !== 'portalSealed' && RESERVED_DECORATIVE_HUES.has(colour.toLowerCase())) {
                    issue(problems, 'reserved-decorative-hue', `${path}.materials.${key}`, 'hostile/heal hues are reserved and cannot decorate a shrine');
                }
            }
            if (profile.materials.interact?.toLowerCase() !== '#c8a23a') {
                issue(problems, 'wrong-interact-cue', `${path}.materials.interact`, 'must reuse the established gold #c8a23a cue');
            }
        }
    }
    const sorted = [...profiles].sort((left, right) => left.rank - right.rank);
    const monotonicFields = [
        'scale',
        'footprintRadius',
        'platformTiers',
        'archLayers',
        'columnPairs',
        'buttressPairs',
        'spireCount',
        'portalWidth',
        'portalHeight',
        'portalIntensity',
    ];
    for (let index = 1; index < sorted.length; index += 1) {
        const previous = sorted[index - 1];
        const current = sorted[index];
        if (current.rank <= previous.rank) {
            issue(problems, 'non-monotonic-shrine-rank', 'shrineProfiles', 'ranks must be unique/increasing');
        }
        for (const field of monotonicFields) {
            if (current[field] < previous[field]) {
                issue(problems, 'non-monotonic-shrine-silhouette', `shrineProfiles.${current.id}.${field}`, `must be >= ${previous.id}.${field}`);
            }
        }
    }
    return problems;
}
export function validateDungeonEntranceAnchors(value, catalog) {
    const problems = [];
    if (!Array.isArray(value) || value.length === 0) {
        issue(problems, 'invalid-entrance-anchors', 'anchors', 'must be a non-empty array');
        return problems;
    }
    const anchors = value;
    for (const repeated of duplicates(anchors)) {
        issue(problems, 'duplicate-id', 'anchors', `duplicate id '${repeated}'`);
    }
    const difficultyIds = new Set(catalog.difficulties.map((profile) => profile.id));
    const themeIds = new Set(catalog.themes.map((theme) => theme.id));
    for (const [index, anchor] of anchors.entries()) {
        const path = `anchors[${index}]`;
        if (!isRecord(anchor)) {
            issue(problems, 'invalid-anchor', path, 'must be an object');
            continue;
        }
        if (!validId(anchor.id))
            issue(problems, 'invalid-id', `${path}.id`, 'must be kebab-case');
        if (!validId(anchor.biomeId)) {
            issue(problems, 'invalid-id', `${path}.biomeId`, 'must be kebab-case');
        }
        if (!finitePosition(anchor.position)) {
            issue(problems, 'invalid-position', `${path}.position`, 'must contain finite x/y/z');
        }
        if (!Number.isInteger(anchor.rotationQuarterTurns) ||
            anchor.rotationQuarterTurns < 0 ||
            anchor.rotationQuarterTurns > 3) {
            issue(problems, 'invalid-rotation', `${path}.rotationQuarterTurns`, 'must be 0, 1, 2, or 3');
        }
        if (!finitePositive(anchor.clearanceRadius)) {
            issue(problems, 'invalid-clearance', `${path}.clearanceRadius`, 'must be > 0');
        }
        if (!Number.isInteger(anchor.targetTier) || anchor.targetTier < 1) {
            issue(problems, 'invalid-target-tier', `${path}.targetTier`, 'must be a positive integer');
        }
        if (!finitePositive(anchor.selectionWeight)) {
            issue(problems, 'invalid-selection-weight', `${path}.selectionWeight`, 'must be > 0');
        }
        if (!Array.isArray(anchor.allowedDifficultyIds) || anchor.allowedDifficultyIds.length === 0) {
            issue(problems, 'invalid-difficulties', `${path}.allowedDifficultyIds`, 'must be non-empty');
        }
        else {
            for (const id of anchor.allowedDifficultyIds) {
                if (!difficultyIds.has(id)) {
                    issue(problems, 'unknown-difficulty', `${path}.allowedDifficultyIds`, String(id));
                }
            }
        }
        if (!Array.isArray(anchor.themeIds) || anchor.themeIds.length === 0) {
            issue(problems, 'invalid-themes', `${path}.themeIds`, 'must be non-empty');
        }
        else {
            for (const id of anchor.themeIds) {
                const theme = catalog.themes.find((entry) => entry.id === id);
                if (!themeIds.has(id) || !theme) {
                    issue(problems, 'unknown-theme', `${path}.themeIds`, String(id));
                }
                else if (anchor.targetTier < theme.tierRange.min ||
                    anchor.targetTier > theme.tierRange.max) {
                    issue(problems, 'theme-tier-mismatch', `${path}.themeIds`, `theme '${id}' does not support tier ${anchor.targetTier}`);
                }
            }
        }
    }
    return problems;
}
export function validateDungeonEnemyIconRules(value) {
    const problems = [];
    if (!Array.isArray(value) || value.length === 0) {
        issue(problems, 'invalid-icon-rules', 'iconRules', 'must be a non-empty array');
        return problems;
    }
    const rules = value;
    for (const repeated of duplicates(rules)) {
        issue(problems, 'duplicate-id', 'iconRules', `duplicate id '${repeated}'`);
    }
    let fallbackCount = 0;
    for (const [index, rule] of rules.entries()) {
        const path = `iconRules[${index}]`;
        if (!isRecord(rule)) {
            issue(problems, 'invalid-icon-rule', path, 'must be an object');
            continue;
        }
        if (!validId(rule.id))
            issue(problems, 'invalid-id', `${path}.id`, 'must be kebab-case');
        if (typeof rule.label !== 'string' || rule.label.length === 0) {
            issue(problems, 'invalid-label', `${path}.label`, 'must be non-empty');
        }
        if (!validId(rule.symbolKey)) {
            issue(problems, 'invalid-symbol-key', `${path}.symbolKey`, 'must be kebab-case');
        }
        if (typeof rule.preparationHint !== 'string' || rule.preparationHint.length === 0) {
            issue(problems, 'invalid-preparation-hint', `${path}.preparationHint`, 'must be non-empty');
        }
        if (!Number.isInteger(rule.priority) || rule.priority < 0) {
            issue(problems, 'invalid-priority', `${path}.priority`, 'must be an integer >= 0');
        }
        if (!Array.isArray(rule.tags) || rule.tags.some((tag) => !validId(tag))) {
            issue(problems, 'invalid-tags', `${path}.tags`, 'must be kebab-case strings');
        }
        if (!Array.isArray(rule.roles) || rule.roles.some((role) => !validId(role))) {
            issue(problems, 'invalid-roles', `${path}.roles`, 'must be kebab-case strings');
        }
        if (rule.fallback) {
            fallbackCount += 1;
            if (rule.tags.length > 0 || rule.roles.length > 0) {
                issue(problems, 'invalid-fallback-rule', path, 'fallback must not carry tags or roles');
            }
        }
        else if (rule.tags.length === 0 && rule.roles.length === 0) {
            issue(problems, 'unmatchable-icon-rule', path, 'needs at least one tag or role');
        }
    }
    if (fallbackCount !== 1) {
        issue(problems, 'fallback-icon-count', 'iconRules', `expected exactly 1 fallback, found ${fallbackCount}`);
    }
    return problems;
}
export function validatePlaceDungeonEntrancesRequest(request, anchors, profiles, catalog) {
    const problems = [
        ...validateDungeonEntranceAnchors(anchors, catalog),
        ...validateDungeonShrineProfiles(profiles, new Set(catalog.difficulties.map((entry) => entry.id))),
    ];
    if (!isRecord(request)) {
        issue(problems, 'invalid-placement-request', 'request', 'must be an object');
        return problems;
    }
    const seed = request['worldSeed'];
    if (!((typeof seed === 'string' && seed.length > 0) ||
        (typeof seed === 'number' && Number.isFinite(seed)))) {
        issue(problems, 'invalid-world-seed', 'request.worldSeed', 'must be a non-empty string or finite number');
    }
    const quotas = request['difficultyQuotas'];
    if (!Array.isArray(quotas) || quotas.length === 0) {
        issue(problems, 'invalid-difficulty-quotas', 'request.difficultyQuotas', 'must be non-empty');
        return problems;
    }
    const profileIds = new Set(profiles.map((profile) => profile.difficultyId));
    const seen = new Set();
    let total = 0;
    for (const [index, quota] of quotas.entries()) {
        const path = `request.difficultyQuotas[${index}]`;
        if (!isRecord(quota) || !validId(quota['difficultyId'])) {
            issue(problems, 'invalid-difficulty-quota', path, 'must contain a kebab-case difficultyId');
            continue;
        }
        const difficultyId = quota['difficultyId'];
        if (seen.has(difficultyId)) {
            issue(problems, 'duplicate-difficulty-quota', path, difficultyId);
        }
        seen.add(difficultyId);
        if (!profileIds.has(difficultyId)) {
            issue(problems, 'unknown-difficulty', `${path}.difficultyId`, `unknown difficulty '${difficultyId}'`);
        }
        if (!Number.isInteger(quota['count']) || quota['count'] < 1) {
            issue(problems, 'invalid-quota-count', `${path}.count`, 'must be a positive integer');
        }
        else {
            total += quota['count'];
            const eligible = anchors.filter((anchor) => anchor.allowedDifficultyIds.includes(difficultyId));
            if (eligible.length < quota['count']) {
                issue(problems, 'insufficient-eligible-anchors', path, `needs ${String(quota['count'])} '${difficultyId}' anchors, found ${eligible.length}`);
            }
        }
    }
    if (total > anchors.length) {
        issue(problems, 'entrance-quota-overflow', 'request.difficultyQuotas', `needs ${total} distinct anchors, found ${anchors.length}`);
    }
    problems.push(...validateDungeonEntranceRadii(request['radii'] ?? DEFAULT_DUNGEON_ENTRANCE_RADII, 'request.radii'));
    return problems;
}
export function validateDungeonEntranceSite(site, profile) {
    const problems = [];
    if (!validId(site.id))
        issue(problems, 'invalid-id', 'site.id', 'must be kebab-case');
    if (!validId(site.anchorId))
        issue(problems, 'invalid-id', 'site.anchorId', 'must be kebab-case');
    if (!finitePosition(site.position))
        issue(problems, 'invalid-position', 'site.position', 'must be finite');
    if (!profile || profile.difficultyId !== site.difficultyId) {
        issue(problems, 'site-profile-mismatch', 'site.difficultyId', site.difficultyId);
    }
    if (!Array.isArray(site.themeIds) || site.themeIds.length === 0) {
        issue(problems, 'invalid-themes', 'site.themeIds', 'must be non-empty');
    }
    problems.push(...validateDungeonEntranceRadii(site.radii, 'site.radii'));
    const material = {
        anchorId: site.anchorId,
        biomeId: site.biomeId,
        position: site.position,
        rotationQuarterTurns: site.rotationQuarterTurns,
        clearanceRadius: site.clearanceRadius,
        targetTier: site.targetTier,
        difficultyId: site.difficultyId,
        themeIds: site.themeIds,
        radii: site.radii,
    };
    if (site.placementFingerprint !== stableFingerprint(material)) {
        issue(problems, 'placement-fingerprint-mismatch', 'site.placementFingerprint', 'does not match stable site material');
    }
    return problems;
}
export function validateDungeonEntranceState(site, state) {
    const problems = [];
    if (state.siteId !== site.id)
        issue(problems, 'state-site-mismatch', 'state.siteId', state.siteId);
    if (!Number.isInteger(state.generationCycle) || state.generationCycle < 0) {
        issue(problems, 'invalid-generation-cycle', 'state.generationCycle', 'must be an integer >= 0');
    }
    if (state.phase === 'primed') {
        if (state.plan.difficultyId !== site.difficultyId) {
            issue(problems, 'primed-difficulty-drift', 'state.plan.difficultyId', 'must equal fixed site difficulty');
        }
        if (state.preview.planFingerprint !== state.plan.fingerprint) {
            issue(problems, 'preview-plan-mismatch', 'state.preview.planFingerprint', 'must equal plan fingerprint');
        }
    }
    return problems;
}
export function validateDungeonEntranceScenePlan(plan) {
    const problems = [];
    if (!Array.isArray(plan.parts)) {
        issue(problems, 'invalid-scene-parts', 'scene.parts', 'must be an array');
        return problems;
    }
    const ids = new Set();
    for (const [index, part] of plan.parts.entries()) {
        const path = `scene.parts[${index}]`;
        if (!validId(part.id))
            issue(problems, 'invalid-id', `${path}.id`, 'must be kebab-case');
        if (ids.has(part.id))
            issue(problems, 'duplicate-id', `${path}.id`, part.id);
        ids.add(part.id);
        if (!DUNGEON_ENTRANCE_PART_KINDS.includes(part.kind)) {
            issue(problems, 'invalid-part-kind', `${path}.kind`, part.kind);
        }
        if (!DUNGEON_ENTRANCE_PRIMITIVES.includes(part.primitive)) {
            issue(problems, 'invalid-primitive', `${path}.primitive`, part.primitive);
        }
        if (!finitePosition(part.position) || !finitePosition(part.scale)) {
            issue(problems, 'invalid-transform', path, 'position and scale must be finite');
        }
    }
    const count = (kind) => plan.parts.filter((part) => part.kind === kind).length;
    if (count('portal') !== 1)
        issue(problems, 'portal-count', 'scene.parts', 'must contain exactly one portal');
    if (count('omen-panel') !== 1)
        issue(problems, 'omen-panel-count', 'scene.parts', 'must contain exactly one omen panel');
    if (count('interact-anchor') !== 1)
        issue(problems, 'interact-anchor-count', 'scene.parts', 'must contain exactly one interact anchor');
    if (count('icon-slot') !== 3)
        issue(problems, 'icon-slot-count', 'scene.parts', 'must contain exactly three icon slots');
    if (plan.portalState === 'ready' && !plan.previewFingerprint) {
        issue(problems, 'ready-without-preview', 'scene.previewFingerprint', 'ready portals require a preview');
    }
    const expected = stableFingerprint({ ...plan, fingerprint: undefined });
    if (plan.fingerprint !== expected) {
        issue(problems, 'scene-fingerprint-mismatch', 'scene.fingerprint', `expected '${expected}'`);
    }
    return problems;
}
//# sourceMappingURL=entrance-validate.js.map